"use client"

import { type QueryKey, type UseQueryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { Result } from "@praha/byethrow"
import { useErrorToast } from "./useErrorToast"

type AppQueryOptions<TRaw, TQueryKey extends QueryKey> = Omit<
  UseQueryOptions<Result.Result<TRaw, string>, unknown, Result.Result<TRaw, string>, TQueryKey>,
  "queryKey" | "queryFn"
> & {
  queryKey: TQueryKey
  queryFn: () => Result.ResultAsync<TRaw, string>
  errorMessage: string
}

export function resultErrorMessage(errorMessage: string, result?: Result.Result<unknown, string | Error>) {
  if (!result || Result.isSuccess(result)) return undefined
  return fullErrorMessage(errorMessage, result.error)
}
const fullErrorMessage = (errorMessage: string, error: unknown) => {
  if (typeof error === "string") {
    return `${errorMessage}: ${error}`
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    const isNetwork = message.includes("network") || message.includes("fetch") || message.includes("load failed") || message.includes("internet")
    if (isNetwork) {
      return `${errorMessage}: похоже, нет интернета`
    }

    return `${errorMessage}: ${message}`
  }

  return `${errorMessage}: что-то пошло не так`
}

// function isUnauthorizedError(error: unknown) {
//   const text = typeof error === "string" ? error : error.message
//   const message = text.toLowerCase()
//   return message.includes("not authenticated") || message.includes("not authorized") || message.includes("unauthorized")
// }
//
export function useAppQuery<TRaw, TQueryKey extends QueryKey = QueryKey>(
  options: AppQueryOptions<TRaw, TQueryKey>,
) {
  const { queryKey, queryFn, errorMessage, ...rest } = options
  const query = useSuspenseQuery<Result.Result<TRaw, string>, unknown, Result.Result<TRaw, string>, TQueryKey>({
    queryKey,
    queryFn: async () => {
      try {
        const result = await queryFn()
        if (Result.isFailure(result)) {
          throw new Error(result.error) // this avoids caching failed results
        }
        return result
      } catch (error) {
        const fullError = fullErrorMessage(errorMessage, error)
        throw new Error(fullError)
      }
    },
    ...rest,
  })

  const result = query.error
    ? Result.fail(query.error instanceof Error ? query.error : new Error(String(query.error)))
    : query.data

  useErrorToast(
    Result.isSuccess(result) ? undefined : fullErrorMessage(errorMessage, result.error),
    `${query.errorUpdatedAt}:${query.dataUpdatedAt}`
  )

  return {
    ...query,
    data: Result.unwrap(query.data),
  }
}
